// src/components/features/ProductImprovementHub/ProductHubHeadliners.js
import React, { useMemo, memo } from 'react';
import {
  Star,
  Calendar,
  AlertCircle,
  TrendingUp,
  Edit,
  Upload,
  Target,
  ListChecks,
  ListX,
  RefreshCw,
  CheckCircle2,
  HelpCircle,
  ArrowRight,
  Ghost,
  Gift,
  Snowflake,
  Sun,
  Flower,
  School,
  Heart,
  Clover,
} from 'lucide-react';

/* ===================== Helpers ===================== */

const clampPercent = (n) => Math.max(0, Math.min(100, Number(n) || 0));
const formatInt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n || 0));

/* ===================== ProgressDonut ===================== */

const ProgressDonut = memo(({ value = 0 }) => {
  const v = clampPercent(value);
  const ringStyle = { backgroundImage: `conic-gradient(#0ea5e9 ${v * 3.6}deg, #e0f2fe 0deg)` };

  return (
    <div className="relative w-14 h-14 shrink-0">
      <div className="absolute inset-0 rounded-full" style={ringStyle} />
      <div className="absolute inset-[6px] rounded-full bg-white grid place-items-center shadow-inner ring-1 ring-sky-100">
        <span className="text-sky-700 font-extrabold text-base leading-none tabular-nums">{Math.round(v)}</span>
      </div>
    </div>
  );
});
ProgressDonut.displayName = 'ProgressDonut';

/* ===================== LongCard ===================== */

const LongCard = memo(({ icon: Icon, title, main, sub, color = 'sky', right = null, children }) => {
  const colorClasses = {
    sky: 'bg-sky-100 text-sky-700',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-800',
    blue: 'bg-blue-100 text-blue-700',
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <header className="flex items-center gap-2">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]} shrink-0`}>
          <Icon size={20} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{title}</div>
          <div className="mt-0.5 flex items-center justify-between gap-3">
            <div className="text-2xl leading-6 font-black text-slate-700 truncate tabular-nums">{main}</div>
          </div>
          {sub ? <div className="text-sm leading-tight text-slate-600 mt-0.5">{sub}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </header>

      {/* Body */}
      {children ? (
        <div className="mt-3 pt-3 border-t border-slate-100 flex-grow flex flex-col space-y-1.5">
          {children}
        </div>
      ) : null}
    </section>
  );
});
LongCard.displayName = 'LongCard';

/* ===================== ClickableRow ===================== */

const ClickableRow = memo(({ icon: Icon, label, value = 0, onClick, color = 'slate' }) => {
  const colorClasses = {
    slate: 'text-slate-600 hover:text-sky-700',
    green: 'text-green-600 hover:text-green-800',
    amber: 'text-amber-600 hover:text-amber-800',
    red: 'text-red-600 hover:text-red-800',
    blue: 'text-blue-600 hover:text-blue-800',
    purple: 'text-purple-600 hover:text-purple-800',
  };

  const Right = (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold w-11 text-right tabular-nums">{formatInt(value)}</span>
      {onClick ? (
        <ArrowRight
          size={16}
          className="transition-transform opacity-0 group-hover:opacity-100 group-hover:translate-x-1"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  if (!onClick) {
    return (
      <div className={`flex items-center justify-between w-full h-7 ${colorClasses[color]}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={16} aria-hidden="true" />
          <span className="text-sm font-semibold truncate">{label}</span>
        </div>
        {Right}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between w-full h-7 group ${colorClasses[color]} focus:outline-none focus:ring-2 focus:ring-sky-300 rounded-md`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={16} aria-hidden="true" />
        <span className="text-sm font-semibold truncate">{label}</span>
      </div>
      {Right}
    </button>
  );
});
ClickableRow.displayName = 'ClickableRow';

/* ===================== KeywordProductRow ===================== */

const KeywordProductRow = memo(({ product, count, onClick }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      title={product?.productTitle || ''}
      aria-label={`Open SEO tracker for ${product?.productTitle || 'product'}`}
      className="flex items-center justify-between w-full h-8 group text-slate-600 hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-300 rounded-md"
    >
      <div className="flex items-center gap-2 min-w-0 pr-4">
        <span className="text-sm font-semibold truncate">{product?.productTitle || '—'}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-bold w-12 text-right tabular-nums">{formatInt(count)}</span>
        <ArrowRight
          size={16}
          className="transition-transform opacity-0 group-hover:opacity-100 group-hover:translate-x-1"
          aria-hidden="true"
        />
      </div>
    </button>
  </li>
));
KeywordProductRow.displayName = 'KeywordProductRow';

/* ===================== Seasonal Icons ===================== */

const SEASONAL_ICONS = {
  Halloween: Ghost,
  Christmas: Gift,
  Winter: Snowflake,
  Summer: Sun,
  Spring: Flower,
  'Back to School': School,
  "Valentine's Day": Heart,
  "St. Patrick's Day": Clover,
};

/* ===================== Headliners ===================== */

const Headliners = ({
  avgAuditScore,
  displayProducts,
  nextFiveSeasons,
  onGoToSeoTracker,
  onFocusHealth,
  onFocusSeasonal,
  onFocusActivity,
  onFocusTracking,
  onBackfill,
  isBackfilling,
}) => {
  const safeProducts = Array.isArray(displayProducts) ? displayProducts : [];

  const counts = useMemo(() => {
    if (!safeProducts.length) {
      return {
        good: 0,
        needs: 0,
        urgent: 0,
        seasonalProducts: (nextFiveSeasons || []).map((s) => ({ name: s.name, count: 0, icon: SEASONAL_ICONS[s.name] || Calendar })),
        rewrittenLast90d: 0,
        uploadedLast90d: 0,
        totalKeywords: 0,
        productsWithKw: 0,
        productsWithoutKw: 0,
        productsInTop30Count: 0,
        top30Products: [],
        totalProducts: 0,
        productsWithoutScrapedTags: 0,
      };
    }

    let good = 0,
      needs = 0,
      urgent = 0,
      rewrittenLast90d = 0,
      uploadedLast90d = 0,
      totalKeywords = 0,
      productsWithKw = 0,
      productsWithoutKw = 0,
      productsInTop30Count = 0,
      productsWithoutScrapedTags = 0;

    const top30Products = [];
    const now = new Date();
    const ninetyDaysAgo = new Date(now);
    ninetyDaysAgo.setDate(now.getDate() - 90);

    const seasonalProducts = (nextFiveSeasons || []).map((season) => {
      const seasonNameLower = (season?.name || '').toLowerCase();
      const count = safeProducts.filter(
        (p) =>
          (p.seasonalTags || []).some((tag) => (tag || '').toLowerCase() === seasonNameLower) ||
          (p._seasonalRefs || []).some((tag) => (tag || '').toLowerCase() === seasonNameLower)
      ).length;
      return { name: season.name, count, icon: SEASONAL_ICONS[season.name] || Calendar };
    });

    safeProducts.forEach((p) => {
      // 1) Health
      const score = Number(p?.audit?.score || 0);
      if (score >= 80) good += 1;
      else if (score >= 50) needs += 1;
      else urgent += 1;

      // 2) Activity (dates may be Firestore Timestamps or ISO strings)
      const lastUpdated = p?.lastUpdated?.toDate ? p.lastUpdated.toDate() : p?.lastUpdated ? new Date(p.lastUpdated) : null;
      const tptUploaded = p?.tptUploadedAt?.toDate ? p.tptUploadedAt.toDate() : p?.tptUploadedAt ? new Date(p.tptUploadedAt) : null;

      if (lastUpdated && lastUpdated > ninetyDaysAgo) rewrittenLast90d += 1;
      if (tptUploaded && tptUploaded > ninetyDaysAgo) uploadedLast90d += 1;

      // 3) Keyword Tracking
      const activeKwCount = p?.seoKpis?.tracked || 0;
      if (activeKwCount > 0) {
        productsWithKw += 1;
        totalKeywords += activeKwCount;
      } else {
        productsWithoutKw += 1;
      }

      // 4) Keyword Performance
      const top30Count = (p?.allKeywords || []).filter((kw) => kw?.curr !== null && kw?.curr <= 30).length;
      if (top30Count > 0) {
        productsInTop30Count += 1;
        top30Products.push({ ...p, top30Count });
      }

      // 5) TPT Tags presence
      const hasGrades = Array.isArray(p?.scrapedGrades) && p.scrapedGrades.length > 0;
      const hasSubjects = Array.isArray(p?.scrapedSubjects) && p.scrapedSubjects.length > 0;
      if (!hasGrades && !hasSubjects) productsWithoutScrapedTags += 1;
    });

    const sortedTop30Products = top30Products.sort((a, b) => b.top30Count - a.top30Count).slice(0, 5);

    return {
      good,
      needs,
      urgent,
      seasonalProducts,
      rewrittenLast90d,
      uploadedLast90d,
      totalKeywords,
      productsWithKw,
      productsWithoutKw,
      productsInTop30Count,
      top30Products: sortedTop30Products,
      totalProducts: safeProducts.length,
      productsWithoutScrapedTags,
    };
  }, [safeProducts, nextFiveSeasons]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {/* 1) Portfolio Health */}
      <LongCard
        icon={Star}
        color="green"
        title="Portfolio Health"
        main={safeProducts.length ? formatInt(avgAuditScore) : '—'}
        sub="Average Audit Score"
        right={<ProgressDonut value={avgAuditScore || 0} />}
      >
        {/* Health header */}
        <div className="text-[13px] leading-tight text-slate-500 font-semibold mb-1.5">Health Breakdown</div>

        {/* Health buckets */}
        <ClickableRow icon={CheckCircle2} label="Good (80+)" value={counts.good} color="green" onClick={() => onFocusHealth?.('Good')} />
        <ClickableRow
          icon={HelpCircle}
          label="Needs Work (50–79)"
          value={counts.needs}
          color="amber"
          onClick={() => onFocusHealth?.('Needs Improvement')}
        />
        <ClickableRow icon={AlertCircle} label="Urgent (0–49)" value={counts.urgent} color="red" onClick={() => onFocusHealth?.('Urgent')} />

        {/* Tag status + refresh */}
        <div className="pt-2 border-t border-slate-100 mt-2 space-y-2">
          <ClickableRow
            icon={ListX}
            label="Products without TPT Tags"
            value={counts.productsWithoutScrapedTags}
            color="slate"
            onClick={() => onFocusTracking?.('unscraped')}
          />
          <p className="text-xs text-slate-500 leading-tight -mt-0.5">Incorrect subject/grade tags? Select "Refresh" below.</p>
          <button
            type="button"
            onClick={onBackfill}
            disabled={isBackfilling}
            className="flex items-center gap-2 text-s font-semibold text-sky-600 hover:text-sky-800 disabled:opacity-50 pt-1 focus:outline-none focus:ring-2 focus:ring-sky-300 rounded-md"
            aria-live="polite"
          >
            <RefreshCw size={14} className={isBackfilling ? 'animate-spin' : ''} aria-hidden="true" />
            {isBackfilling ? 'Refreshing Tags…' : 'Refresh All Product Tags'}
          </button>
        </div>
      </LongCard>

      {/* 2) Seasonal Spotlight */}
      <LongCard
        icon={Calendar}
        color="purple"
        title="Seasonal Spotlight"
        main={nextFiveSeasons && nextFiveSeasons[0] ? `${formatInt(nextFiveSeasons[0].days)} days` : '—'}
        sub={nextFiveSeasons && nextFiveSeasons[0] ? `until ${nextFiveSeasons[0].name}` : 'No upcoming season'}
      >
        <p className="text-[13px] leading-tight text-slate-500 font-semibold mb-1.5">Products Tagged for Upcoming Seasons:</p>
        {counts.seasonalProducts.map((season) => (
          <ClickableRow
            key={season.name}
            icon={season.icon}
            label={season.name}
            value={season.count}
            color="purple"
            onClick={() => onFocusSeasonal?.(season.name)}
          />
        ))}
      </LongCard>

      {/* 3) Activity & Tracking */}
      <LongCard
        icon={Target}
        color="blue"
        title="Activity & Tracking"
        main={formatInt(counts.totalKeywords)}
        sub="Total Keywords Tracked"
      >
        <div className="text-[13px] leading-tight text-slate-500 font-semibold mb-1.5">
          Recent Activity (Last 90 days)
        </div>
        <ClickableRow
          icon={Edit}
          label="Descriptions Rewritten"
          value={counts.rewrittenLast90d}
          color="blue"
          onClick={() => onFocusActivity?.('rewritten')}
        />
        <ClickableRow
          icon={Upload}
          label="Uploaded to TPT"
          value={counts.uploadedLast90d}
          color="blue"
          onClick={() => onFocusActivity?.('uploaded')}
        />

        <div className="pt-2 mt-2 border-t border-slate-100" />

        <div className="text-[13px] leading-tight text-slate-500 font-semibold mb-1.5">
          Ranking Tracking
        </div>
        <ClickableRow
          icon={ListChecks}
          label="Products Ranking Tracked"
          value={counts.productsWithKw}
          color="slate"
          onClick={() => onFocusTracking?.('tracked')}
        />
        <ClickableRow
          icon={ListX}
          label="Products Not Ranking Tracked"
          value={counts.productsWithoutKw}
          color="slate"
          onClick={() => onFocusTracking?.('untracked')}
        />
      </LongCard>

      {/* 4) Keyword Performance */}
      <LongCard
        icon={TrendingUp}
        color="sky"
        title="Keyword Performance"
        main={formatInt(counts.productsInTop30Count)}
        sub="Products with Top 30 Keywords"
      >
        {counts.top30Products.length > 0 ? (
          <>
            <div className="text-[13px] leading-tight text-slate-500 font-semibold mb-1.5">
              Leaders (by # Top 30 keywords)
            </div>
            <ul className="space-y-1">
              {counts.top30Products.map((p) => (
                <KeywordProductRow
                  key={`top30-${p.id || p.productId || p.productTitle}`}
                  product={p}
                  count={p.top30Count}
                  onClick={() => onGoToSeoTracker?.(p)}
                />
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm text-slate-400">No products found with Top 30 keywords.</p>
        )}
      </LongCard>
    </div>
  );
};

export default Headliners;
